/**
 * What "Create lockfiles" did, per service (vfs-service POST /quality/lockfiles, lockfileCreate.js): Flow runs each
 * service's own package manager in the sandbox (npm, pnpm, Yarn, uv, poetry, pipenv, pip, go, cargo, dotnet, gradle),
 * lockfile only, and saves what it writes as a commit; no AI. Each row: created <file> (with the command), already
 * there, the tool that is not installed, the service to start first, or the failure with the tool's last lines.
 *
 * `LockfileStatus` is what shows where Create lockfiles was clicked (the overview's step or card, the lockfile banner of
 * a dependency check), and only there: the progress while it runs (the services it starts first, then the lockfiles
 * being made, with Stop), then the result.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Banner } from './QualityCommon';
import { plural } from './qualityModel';

const SHOWN = ['created', 'refreshed', 'tool_missing', 'not_ready', 'unsupported', 'error'];

/** One row per result worth showing: `{ key, tone, service, text, command?, output? }`. */
export function lockfileRows(results = []) {
  return results.filter((r) => SHOWN.includes(r.status)).map((r, i) => {
    const key = `${r.service}-${r.manifest || ''}-${i}`;
    if (r.status === 'created' || r.status === 'refreshed') {
      return { key, tone: 'pass', service: r.service, text: `${r.status === 'created' ? 'Created' : 'Refreshed'} ${r.file || 'its lockfile'}`, command: r.tool || null };
    }
    if (r.status === 'tool_missing') {
      return { key, tone: 'warn', service: r.service, text: `${r.missingTool || 'Its package manager'} is not installed: ${r.message}` };
    }
    if (r.status === 'not_ready') { return { key, tone: 'warn', service: r.service, text: r.message }; }
    return { key, tone: r.status === 'error' ? 'fail' : 'warn', service: r.service, text: r.network ? `${r.message}. Check the sandbox's internet access, then try again.` : r.message, command: r.tool || null, output: r.output || null };
  });
}

/** The result's first line: what was made, or why nothing was. */
export function lockfileHeadline(note, rows) {
  if (note.error) { return `Could not create the lockfiles: ${note.error}`; }
  const created = note.created ?? (note.results || []).filter((r) => r.status === 'created').length;
  const refreshed = note.refreshed ?? (note.results || []).filter((r) => r.status === 'refreshed').length;
  const made = [created ? `Created ${plural(created, 'lockfile')}` : null, refreshed ? `refreshed ${plural(refreshed, 'lockfile')}` : null].filter(Boolean).join(', ');
  if (note.cancelled) { return made ? `Stopped. ${made} before it stopped.` : 'Stopped: no lockfile was created.'; }
  if (made) { return `${made.charAt(0).toUpperCase()}${made.slice(1)}. Lockfiles saved in the project (see History).${note.rerun ? ' Checking the dependencies again…' : ''}`; }
  return rows.length ? 'No lockfile created:' : 'No lockfile to create: no service declares dependencies without one.';
}

/** The banner after Create lockfiles ran (or failed as a whole). */
export default function LockfileResults({ note, onDismiss }) {
  const rows = lockfileRows(note.results);
  const problems = rows.filter((r) => r.tone !== 'pass').length;
  return (
    <Banner onDismiss={onDismiss} testId="quality-lockfile-note" tone={problems || note.error ? 'warn' : 'info'}>
      <span data-testid="quality-lockfile-headline">{lockfileHeadline(note, rows)}</span>
      {rows.length > 0 && (
        <ul aria-label="Lockfiles per service" className="quality-lockfile-rows">
          {rows.map((row) => (
            <li className={`is-${row.tone}`} data-testid={`quality-lockfile-${row.service}`} key={row.key}>
              <strong>{row.service}</strong> {row.text}
              {row.command && <span className="quality-mono quality-small quality-muted"> · {row.command}</span>}
              {row.output && <pre className="quality-lockfile-output">{row.output}</pre>}
            </li>
          ))}
        </ul>
      )}
    </Banner>
  );
}

LockfileResults.propTypes = {
  note: PropTypes.shape({
    cancelled: PropTypes.bool, created: PropTypes.number, error: PropTypes.string, refreshed: PropTypes.number, rerun: PropTypes.bool, results: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const SERVICE_STATES = { waiting: 'waiting', starting: 'starting…', ready: 'ready', failed: 'could not start' };

/** While Create lockfiles runs: the call, the services it starts first (`job.state` starting), then the lockfiles. */
export function LockfileProgress({ job = null, onStop = null }) {
  const names = (job?.services || []).map((s) => s.service);
  let text;
  if (job?.state === 'starting') {
    text = <><strong>Starting {names.join(', ')}…</strong> then creating the lockfiles: {names.length === 1 ? 'it was' : 'they were'} not started since the sandbox woke, and each service&apos;s own package manager runs where it is installed.</>;
  } else if (job?.state === 'creating') {
    text = <><strong>Creating the lockfiles…</strong> each service&apos;s own package manager runs in your sandbox; this can take a minute.</>;
  } else {
    text = <><strong>Creating lockfiles…</strong> each service&apos;s own package manager runs in your sandbox (npm, pnpm, Yarn, uv, go, cargo…); this can take a minute.</>;
  }
  return (
    <div aria-live="polite" className="quality-progress" data-testid="quality-lockfile-busy" role="status">
      <span aria-hidden className="quality-spinner" />
      <span className="quality-progress-text">{text}</span>
      {job?.state === 'starting' && (
        <ul aria-label="Services" className="quality-progress-list">
          {job.services.map((s) => (
            <li className={`is-${s.state === 'ready' ? 'done' : s.state === 'failed' ? 'failed' : 'running'}`} key={s.service} title={s.message || undefined}>{s.service} {SERVICE_STATES[s.state] || s.state}</li>
          ))}
        </ul>
      )}
      {job && onStop && <button className="quality-btn is-sm" data-testid="quality-lockfile-stop" onClick={onStop} type="button">Stop</button>}
    </div>
  );
}

LockfileProgress.propTypes = { job: PropTypes.object, onStop: PropTypes.func };

/** Create lockfiles where it was clicked: its progress, then its result. `state`: `{ busy, job, note }` (QualityPanel). */
export function LockfileStatus({ state, onDismiss, onStop = null }) {
  if (state.note) { return <LockfileResults note={state.note} onDismiss={onDismiss} />; }
  if (state.busy || state.job) { return <LockfileProgress job={state.job} onStop={onStop} />; }
  return null;
}

LockfileStatus.propTypes = { onDismiss: PropTypes.func.isRequired, onStop: PropTypes.func, state: PropTypes.object.isRequired };
